import { Table } from '@tanstack/react-table';
import { Input } from '@ui/components';
import { useTableExport } from './lib/use-table-export';
import { cn } from '@ui/lib/utils';
import { useCallback } from 'react';
import { useAppTableContext } from '../../lib/context/use-app-table-context';
import { DeleteButton } from './ui/delete-button';
import { ExportButton } from './ui/export-button';
import { ImportButton } from './ui/import-button';
import { NewButton } from './ui/new-button';
import { ViewButton } from './ui/view-button';

interface AppTableToolbarProps<TData> {
  table: Table<TData>;
  className?: string;
}

export const AppTableToolbar = <TData,>({
  table,
  className,
}: AppTableToolbarProps<TData>) => {
  const {
    onAddNew,
    onImport,
    onDelete,
    onExcel,
    onPdf,
    CustomToolbar,
    CustomFilter,
  } = useAppTableContext();

  const isEnableSearch = table.options.enableGlobalFilter;
  const searchValue = table.getState().globalFilter ?? '';

  const isSelected =
    Object.keys(table.getState().rowSelection ?? {}).length > 0;

  const { handleExportExcel, handleExportPdf } = useTableExport(
    table,
    onExcel,
    onPdf,
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      table.setGlobalFilter(String(e.target.value));
      table.setPageIndex(0);
    },
    [table],
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-4 p-4', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {onAddNew && <NewButton onAddNew={onAddNew} />}
        {onImport && <ImportButton onImport={onImport} />}

        {isEnableSearch && (
          <Input
            className="h-8 w-[220px] text-sm"
            placeholder="Search..."
            value={searchValue}
            onChange={handleInputChange}
          />
        )}

        {!!CustomFilter && CustomFilter}
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {!!CustomToolbar && CustomToolbar}

        {(!!onExcel || !!onPdf) && (
          <ExportButton
            onExcel={onExcel ? handleExportExcel : undefined}
            onPdf={onPdf ? handleExportPdf : undefined}
          />
        )}

        {!!onDelete && isSelected && <DeleteButton onClick={onDelete} />}

        <ViewButton table={table} />
      </div>
    </div>
  );
};
